/**
 * Tríadas Engine — SET-inspired card game
 * 81 cards × 4 attributes: symbol, color, fill, amount
 */

export type CardSymbol = 'cerebro' | 'logica' | 'neurona';
export type CardColor  = 'verde' | 'azul' | 'rojo';
export type CardFill   = 'rayado' | 'solido' | 'vacio';
export type CardAmount = 1 | 2 | 3;
export type CardAttribute = 'symbol' | 'color' | 'fill' | 'amount';

export interface TriasCard {
  symbol: CardSymbol;
  color:  CardColor;
  fill:   CardFill;
  amount: CardAmount;
  image:  string;
  id:     string;
}

// ─── Card catalog ─────────────────────────────────────────────────────────────

const SYMBOLS: CardSymbol[] = ['cerebro', 'logica', 'neurona'];
const COLORS:  CardColor[]  = ['verde', 'azul', 'rojo'];
const FILLS:   CardFill[]   = ['rayado', 'solido', 'vacio'];
const AMOUNTS: CardAmount[] = [1, 2, 3];

export const ALL_CARDS: TriasCard[] = (() => {
  const cards: TriasCard[] = [];
  for (const symbol of SYMBOLS)
    for (const color of COLORS)
      for (const fill of FILLS)
        for (const amount of AMOUNTS)
          cards.push({
            symbol, color, fill, amount,
            image: `/img/${symbol}_${color}_${fill}_${amount}.svg`,
            id: `${symbol}_${color}_${fill}_${amount}`,
          });
  return cards;
})();

// ─── Difficulty config ────────────────────────────────────────────────────────

export const DIFFICULTY_CONFIG: Record<number, {
  name: string;
  icon: string;
  activeAttributes: CardAttribute[];
}> = {
  1: { name: 'Sinapsis', icon: '⚡', activeAttributes: ['symbol', 'color'] },
  2: { name: 'Cerebro',  icon: '🧩', activeAttributes: ['symbol', 'color', 'amount'] },
  3: { name: 'Genio',    icon: '💡', activeAttributes: ['symbol', 'color', 'amount', 'fill'] },
  4: { name: 'Einstein', icon: '🎓', activeAttributes: ['symbol', 'color', 'amount', 'fill'] },
};

// ─── Core logic ───────────────────────────────────────────────────────────────

function shuffle<T>(arr: T[]): T[] {
  const r = [...arr];
  for (let i = r.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [r[i], r[j]] = [r[j], r[i]];
  }
  return r;
}

export function isValidSet(
  cards: [TriasCard, TriasCard, TriasCard],
  activeAttributes: CardAttribute[],
): boolean {
  for (const attr of activeAttributes) {
    const values = cards.map(c => c[attr]);
    if (new Set(values).size === 2) return false; // two same, one different
  }
  return true;
}

export function hasValidSet(board: TriasCard[], activeAttributes: CardAttribute[]): boolean {
  const n = board.length;
  for (let i = 0; i < n - 2; i++)
    for (let j = i + 1; j < n - 1; j++)
      for (let k = j + 1; k < n; k++)
        if (isValidSet([board[i], board[j], board[k]], activeAttributes))
          return true;
  return false;
}

function countValidSets(board: TriasCard[], activeAttributes: CardAttribute[]): number {
  let count = 0;
  const n = board.length;
  for (let i = 0; i < n - 2; i++)
    for (let j = i + 1; j < n - 1; j++)
      for (let k = j + 1; k < n; k++)
        if (isValidSet([board[i], board[j], board[k]], activeAttributes))
          count++;
  return count;
}

// ─── Game creation ────────────────────────────────────────────────────────────

export function createGame(difficulty: number): { board: TriasCard[]; deck: TriasCard[] } {
  const { activeAttributes } = DIFFICULTY_CONFIG[difficulty] ?? DIFFICULTY_CONFIG[3];
  const einsteinMode = difficulty === 4;

  let bestBoard: TriasCard[] = [];
  let bestDeck: TriasCard[]  = [];
  let bestCount = Infinity;

  const attempts = einsteinMode ? 80 : 20;

  for (let i = 0; i < attempts; i++) {
    const shuffled = shuffle(ALL_CARDS);
    const board = shuffled.slice(0, 12);
    const deck  = shuffled.slice(12);
    if (!hasValidSet(board, activeAttributes)) continue;

    if (!einsteinMode) return { board, deck };

    const count = countValidSets(board, activeAttributes);
    if (count < bestCount) {
      bestCount = count;
      bestBoard = board;
      bestDeck  = deck;
      if (count <= 2) break;
    }
  }

  if (bestBoard.length > 0) return { board: bestBoard, deck: bestDeck };

  // Fallback: keep trying until we get a board with at least one valid set
  for (let i = 0; i < 500; i++) {
    const shuffled = shuffle(ALL_CARDS);
    const board = shuffled.slice(0, 12);
    const deck  = shuffled.slice(12);
    if (hasValidSet(board, activeAttributes)) return { board, deck };
  }

  const shuffled = shuffle(ALL_CARDS);
  return { board: shuffled.slice(0, 12), deck: shuffled.slice(12) };
}

// ─── Board operations ─────────────────────────────────────────────────────────

export function processFoundSet(
  board: TriasCard[],
  deck: TriasCard[],
  foundIndices: [number, number, number],
): { board: TriasCard[]; deck: TriasCard[] } {
  const newDeck  = [...deck];
  const newBoard = [...board];
  const toRemove: number[] = [];

  for (const idx of foundIndices) {
    if (newDeck.length > 0) {
      newBoard[idx] = newDeck.shift()!;
    } else {
      toRemove.push(idx);
    }
  }

  for (const idx of toRemove.sort((a, b) => b - a)) {
    newBoard.splice(idx, 1);
  }

  return { board: newBoard, deck: newDeck };
}

export function addThreeCards(
  board: TriasCard[],
  deck: TriasCard[],
): { board: TriasCard[]; deck: TriasCard[] } {
  const newDeck  = [...deck];
  const newBoard = [...board];
  const count = Math.min(3, newDeck.length);
  for (let i = 0; i < count; i++) newBoard.push(newDeck.shift()!);
  return { board: newBoard, deck: newDeck };
}
